import { proxy } from 'valtio';

import { diagramValidationState } from '@diagram/state/diagram-validation';
import { validationState } from '@diagram/state/validation';
import { componentInstanceValidator } from '@diagram/validators/component-instances/component-instance-validator';
import { formatters } from '@diagram/validators/zones/formatters';

import { ComponentInstanceValidationError, ValidationFeature, ValidationType, ZoneValidationError } from 'models';

enum ValidationView {
    OVERVIEW = 'overview',
    QUICKFIXES = 'quickfixes',
}

const validationTypeLevels: Record<Exclude<ValidationType, 'all'>, string> = {
    errors: 'error',
    warnings: 'warning',
};

const ValidationService = {
    state: proxy<{
        view: ValidationView;
        type: ValidationType;
        feature: ValidationFeature | null;
    }>({
        view: ValidationView.OVERVIEW,
        type: 'all',
        feature: null,
    }),

    setView: (view: ValidationView) => {
        ValidationService.state.view = view;
    },

    setType: (type: ValidationType) => {
        ValidationService.state.type = type;
    },

    setFeature: (feature: ValidationFeature | null) => {
        ValidationService.state.feature = feature;
    },

    filterErrorsByType: <T extends { level: string }>(errors: T[], type: ValidationType): T[] => {
        if (type === 'all') {
            return errors;
        }

        return errors.filter((error) => error.level === validationTypeLevels[type]);
    },

    filterZoneErrorsByFeature: (errors: ZoneValidationError[], feature: ValidationFeature | null) => {
        if (!feature) {
            return errors;
        }

        return errors.filter((error) => formatters[error.type](error).features?.[feature]);
    },

    filterComponentInstanceErrorsByFeature: (
        errors: ComponentInstanceValidationError[],
        feature: ValidationFeature | null,
    ) => {
        if (!feature) {
            return errors;
        }

        return errors.filter((error) => componentInstanceValidator.formatters[error.type](error).features?.[feature]);
    },

    activateZone: (zoneId: string) => {
        validationState.activeZone = zoneId;
        validationState.activeComponentInstance = null;
    },

    deactivateZone: () => {
        validationState.activeZone = null;
        validationState.activeComponentInstance = null;
    },

    activateComponentInstance: (componentInstanceId: string) => {
        validationState.activeZone = null;
        validationState.activeComponentInstance = componentInstanceId;
    },

    deactivateComponentInstance: () => {
        validationState.activeZone = null;
        validationState.activeComponentInstance = null;
    },

    getComponentInstanceErrors: (
        componentInstanceErrors: ComponentInstanceValidationError[],
        componentInstanceId: string,
    ) => {
        return componentInstanceErrors.filter((error) => {
            return error.payload.componentInstance.id === componentInstanceId && error.level === 'error';
        });
    },

    getComponentInstanceWarnings: (
        componentInstanceErrors: ComponentInstanceValidationError[],
        componentInstanceId: string,
    ) => {
        return componentInstanceErrors.filter((error) => {
            return error.payload.componentInstance.id === componentInstanceId && error.level === 'warning';
        });
    },

    getValidationReport: () => {
        const validations: {
            title: string;
            description: string;
        }[] = [];

        diagramValidationState.errors.componentInstances.forEach((error) => {
            const formatter = componentInstanceValidator.formatters[error.type];
            const { title, description } = formatter(error);

            validations.push({
                title,
                description,
            });
        });

        diagramValidationState.errors.zones.control.forEach((error) => {
            const formatter = formatters[error.type];
            const { title, description } = formatter(error);

            validations.push({
                title,
                description,
            });
        });

        return validations;
    },
};

export { ValidationService, ValidationView };
export type { ValidationType };
